import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { apiRawFetch } from '../../main/client';

interface TokenListItem {
    id: number;
    name: string;
    scope: string;
    lastUsed: string | null;
    created: string;
}

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        '& input': { backgroundColor: '#0f0f23' },
        '& fieldset': { borderWidth: '2px', borderColor: '#374151' },
        '&:hover fieldset': { borderWidth: '2px', borderColor: '#6366f1' },
        '&.Mui-focused fieldset': { borderWidth: '3px', borderColor: '#6366f1' },
    },
    '& .MuiInputLabel-root': {
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '1rem',
        letterSpacing: '0.05em',
    },
};

const cellSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    px: 1,
};

const PersonalAccessTokens = () => {
    const [tokens, setTokens] = useState<TokenListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newTokenName, setNewTokenName] = useState('');
    const [newTokenScope, setNewTokenScope] = useState<'readonly' | 'operations'>('readonly');
    const [creating, setCreating] = useState(false);
    const [createdToken, setCreatedToken] = useState<string | null>(null);
    const [copiedToken, setCopiedToken] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);

    const fetchTokens = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiRawFetch('api/personal-access-tokens');
            if (!response.ok) {
                throw new Error('Failed to fetch tokens');
            }
            const data = await response.json();
            setTokens(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTokens();
    }, [fetchTokens]);

    const handleCreate = async () => {
        if (!newTokenName.trim()) return;

        try {
            setCreating(true);
            setError(null);

            const response = await apiRawFetch('api/personal-access-tokens', 'POST', {
                name: newTokenName.trim(),
                scope: newTokenScope,
            });

            if (!response.ok) {
                const data = (await response.json().catch(() => ({}))) as { error?: string };
                throw new Error(data.error ?? 'Failed to create token');
            }

            const data = await response.json();
            setCreatedToken(data.token);
            setNewTokenName('');
            setNewTokenScope('readonly');
            await fetchTokens();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setCreating(false);
        }
    };

    const handleCopyToken = async () => {
        if (!createdToken) return;
        try {
            await navigator.clipboard.writeText(createdToken);
            setCopiedToken(true);
            setTimeout(() => setCopiedToken(false), 2000);
        } catch {
            setError('Failed to copy to clipboard');
        }
    };

    const handleDelete = async (tokenId: number) => {
        try {
            setDeleting(true);
            setError(null);

            const response = await apiRawFetch(`api/personal-access-tokens/${tokenId}`, 'DELETE');

            if (!response.ok) {
                const data = (await response.json().catch(() => ({}))) as { error?: string };
                throw new Error(data.error ?? 'Failed to revoke token');
            }

            setDeleteConfirmId(null);
            await fetchTokens();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#e5e7eb',
                    }}
                >
                    Personal Access Tokens
                </Typography>
                <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogOpen(true)}
                    sx={{
                        borderRadius: 0,
                        border: '2px solid',
                        borderColor: 'secondary.main',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 2,
                        py: 0.5,
                    }}
                >
                    Create Token
                </Button>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{
                        borderRadius: 0,
                        border: '3px solid #ef4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        fontWeight: 600,
                        mb: 2,
                        '& .MuiAlert-icon': { color: '#ef4444' },
                    }}
                >
                    {error}
                </Alert>
            )}

            {tokens.length === 0 ? (
                <Box sx={{ border: '2px solid #374151', p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        No personal access tokens
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.75rem' }}>
                        Create a token to allow external tools like OpenClaw to access MushRoom&apos;s API
                    </Typography>
                </Box>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ borderBottom: '2px solid #374151' }}>
                            <TableCell sx={cellSx}>Name</TableCell>
                            <TableCell sx={cellSx}>Scope</TableCell>
                            <TableCell sx={cellSx}>Last Used</TableCell>
                            <TableCell sx={{ ...cellSx, width: 100 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tokens.map(token => (
                            <TableRow key={token.id} sx={{ borderBottom: '1px solid #374151' }}>
                                <TableCell sx={{ px: 1 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {token.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', fontSize: '0.65rem', fontFamily: 'monospace' }}
                                    >
                                        Created {new Date(token.created).toLocaleDateString()}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ px: 1 }}>
                                    <Chip
                                        label={token.scope}
                                        size="small"
                                        sx={{
                                            borderRadius: 0,
                                            fontWeight: 700,
                                            fontSize: '0.65rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            backgroundColor:
                                                token.scope === 'readonly'
                                                    ? 'rgba(99, 102, 241, 0.2)'
                                                    : 'rgba(245, 158, 11, 0.2)',
                                            color: token.scope === 'readonly' ? '#818cf8' : '#fbbf24',
                                            border: `1px solid ${token.scope === 'readonly' ? '#6366f1' : '#f59e0b'}`,
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ px: 1 }}>
                                    <Typography variant="body2">
                                        {token.lastUsed ? new Date(token.lastUsed).toLocaleString() : 'Never'}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ px: 1 }}>
                                    <Tooltip title="Revoke this token">
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() => setDeleteConfirmId(token.id)}
                                            sx={{
                                                borderRadius: 0,
                                                borderWidth: '2px',
                                                minWidth: 'auto',
                                                px: 1,
                                                '&:hover': {
                                                    borderWidth: '2px',
                                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                },
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </Button>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {/* Create Dialog */}
            <Dialog
                open={dialogOpen && !createdToken}
                onClose={() => !creating && setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 0, border: '4px solid #6366f1', backgroundColor: '#1a1a2e' },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: '3px solid #6366f1',
                        backgroundColor: '#0f0f23',
                        fontWeight: 900,
                        fontSize: '1.25rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                    }}
                >
                    Create Personal Access Token
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: '#1a1a2e', p: 0 }}>
                    <Stack sx={{ py: 3, px: 3 }} spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            Create a token to allow external tools to access the MushRoom API on your behalf.
                        </Typography>
                        <TextField
                            label="Name"
                            placeholder="e.g., OpenClaw, Home Assistant"
                            fullWidth
                            value={newTokenName}
                            onChange={e => setNewTokenName(e.target.value)}
                            disabled={creating}
                            autoFocus
                            sx={inputSx}
                        />
                        <FormControl fullWidth>
                            <InputLabel
                                sx={{
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '1rem',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                Scope
                            </InputLabel>
                            <Select
                                value={newTokenScope}
                                label="Scope"
                                onChange={e => setNewTokenScope(e.target.value as 'readonly' | 'operations')}
                                disabled={creating}
                                sx={{
                                    borderRadius: 0,
                                    '& fieldset': { borderWidth: '2px', borderColor: '#374151' },
                                    '&:hover fieldset': { borderWidth: '2px', borderColor: '#6366f1' },
                                    '&.Mui-focused fieldset': { borderWidth: '3px', borderColor: '#6366f1' },
                                }}
                            >
                                <MenuItem value="readonly">Readonly - GET requests only</MenuItem>
                                <MenuItem value="operations">Operations - Read and write access</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 2,
                        gap: 1.5,
                    }}
                >
                    <Button
                        onClick={() => setDialogOpen(false)}
                        disabled={creating}
                        variant="outlined"
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                            '&:hover': { borderWidth: '2px', borderColor: '#374151', backgroundColor: 'rgba(55, 65, 81, 0.1)' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreate}
                        disabled={creating || !newTokenName.trim()}
                        variant="contained"
                        color="primary"
                        sx={{
                            borderRadius: 0,
                            border: '2px solid #6366f1',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                        }}
                    >
                        {creating ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Token Created Dialog - shows the token value once */}
            <Dialog
                open={createdToken !== null}
                onClose={() => {
                    setCreatedToken(null);
                    setDialogOpen(false);
                    setCopiedToken(false);
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 0, border: '4px solid #22c55e', backgroundColor: '#1a1a2e' },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: '3px solid #22c55e',
                        backgroundColor: '#0f0f23',
                        fontWeight: 900,
                        fontSize: '1.25rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                    }}
                >
                    Token Created
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: '#1a1a2e', p: 0 }}>
                    <Stack sx={{ py: 3, px: 3 }} spacing={2}>
                        <Alert
                            severity="warning"
                            sx={{
                                borderRadius: 0,
                                border: '3px solid #f59e0b',
                                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                fontWeight: 600,
                                '& .MuiAlert-icon': { color: '#f59e0b' },
                            }}
                        >
                            Copy this token now. You won&apos;t be able to see it again.
                        </Alert>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                border: '2px solid #374151',
                                backgroundColor: '#0f0f23',
                                p: 1.5,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    wordBreak: 'break-all',
                                    flex: 1,
                                    color: '#22c55e',
                                }}
                            >
                                {createdToken}
                            </Typography>
                            <Tooltip title={copiedToken ? 'Copied!' : 'Copy token'}>
                                <Button
                                    variant={copiedToken ? 'contained' : 'outlined'}
                                    color={copiedToken ? 'success' : 'primary'}
                                    size="small"
                                    onClick={handleCopyToken}
                                    sx={{
                                        borderRadius: 0,
                                        borderWidth: '2px',
                                        minWidth: 'auto',
                                        px: 1,
                                        '&:hover': { borderWidth: '2px' },
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </Button>
                            </Tooltip>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 2,
                    }}
                >
                    <Button
                        onClick={() => {
                            setCreatedToken(null);
                            setDialogOpen(false);
                            setCopiedToken(false);
                        }}
                        variant="contained"
                        color="primary"
                        sx={{
                            borderRadius: 0,
                            border: '2px solid #6366f1',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                        }}
                    >
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmId !== null}
                onClose={() => !deleting && setDeleteConfirmId(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 0, border: '4px solid #ef4444', backgroundColor: '#1a1a2e' },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: '3px solid #ef4444',
                        backgroundColor: '#0f0f23',
                        fontWeight: 900,
                        fontSize: '1.25rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                    }}
                >
                    Revoke Token
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: '#1a1a2e', p: 0 }}>
                    <Stack sx={{ py: 3, px: 3 }} spacing={2}>
                        <Alert
                            severity="warning"
                            sx={{
                                borderRadius: 0,
                                border: '3px solid #f59e0b',
                                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                fontWeight: 600,
                                '& .MuiAlert-icon': { color: '#f59e0b' },
                            }}
                        >
                            This will immediately invalidate the token. Any integrations using it will stop working.
                        </Alert>
                        <Typography variant="body2">
                            Are you sure you want to revoke &quot;
                            {tokens.find(t => t.id === deleteConfirmId)?.name}&quot;?
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 2,
                        gap: 1.5,
                    }}
                >
                    <Button
                        onClick={() => setDeleteConfirmId(null)}
                        disabled={deleting}
                        variant="outlined"
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                            '&:hover': { borderWidth: '2px', borderColor: '#374151', backgroundColor: 'rgba(55, 65, 81, 0.1)' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
                        disabled={deleting}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 0,
                            border: '2px solid',
                            borderColor: 'error.main',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                        }}
                    >
                        {deleting ? 'Revoking...' : 'Revoke'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PersonalAccessTokens;
